import { useMemo } from 'react';

import { useInfiniteIntegrations } from 'services/repositories/integrations/integrations';
import { Connectors, ConnectorsType, IntegrationDTO } from 'support/types';

// Connectors that support manual document import. Data-driven on purpose: when the backend
// adds more, only this list changes (and ideally moves behind an integration capability flag).
const SUPPORTED_IMPORT_CONNECTORS: Array<ConnectorsType> = [Connectors.XENTRAL_V3];

// Integration states we treat as active enough to import from: live (READY) or still being
// set up / tested (IN_TESTING). REQUESTED isn't connected yet, and DEACTIVATED / UNSUPPORTED
// can't import. The backend import endpoint doesn't gate on state, so this is a UI-only guard.
const ACTIVE_IMPORT_STATES: Array<IntegrationDTO['state']> = ['READY', 'IN_TESTING'];

/**
 * The current partner's integrations that can pull documents from their source system: a supported
 * connector (Xentral) in an active state. Drives both the header button's visibility and the modal's
 * source-system picker — no list filters involved. An empty array means the partner has nothing to
 * import from, so the affordance stays hidden.
 */
export const useImportableIntegrations = (): Array<IntegrationDTO> => {
  // perPage bumped so a partner with many integrations still surfaces its Xentral one from the
  // first page (we only read loaded pages here, not the full infinite list).
  const { data } = useInfiniteIntegrations({ variables: { query: { perPage: '100' } } });

  return useMemo(() => {
    const integrations = data?.pages.flatMap((page) => page.items) ?? [];
    return integrations.filter(
      (integration) =>
        ACTIVE_IMPORT_STATES.includes(integration.state) && SUPPORTED_IMPORT_CONNECTORS.includes(integration.connector),
    );
  }, [data]);
};
